import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatAlignCenterOutlinedIcon from '@mui/icons-material/FormatAlignCenterOutlined';
import FormatAlignJustifyOutlinedIcon from '@mui/icons-material/FormatAlignJustifyOutlined';
import FormatAlignLeftOutlinedIcon from '@mui/icons-material/FormatAlignLeftOutlined';
import FormatAlignRightOutlinedIcon from '@mui/icons-material/FormatAlignRightOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatColorTextOutlinedIcon from '@mui/icons-material/FormatColorTextOutlined';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import LooksOutlinedIcon from '@mui/icons-material/LooksOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import { default as Button, default as MuiButton } from '@mui/material/Button';
import MuiButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import MuiCircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import FormControl from '@mui/material/FormControl';
import type { FormControlLabelProps } from '@mui/material/FormControlLabel';
import MuiFormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MuiMenu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import type { SelectChangeEvent } from '@mui/material/Select';
import MuiSelect from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import MuiSwitch from '@mui/material/Switch';
import MuiTextField from '@mui/material/TextField';
import MuiToggleButton from '@mui/material/ToggleButton';
import MuiToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Toolbar from '@mui/material/Toolbar';
import type { TooltipProps } from '@mui/material/Tooltip';
import MuiTooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFetcher, useFetchers, useLoaderData, useNavigate, useNavigation, useSubmit } from '@remix-run/react';
import type { Transform } from 'fabric/fabric-impl';
import * as React from 'react';
import { HexColorPicker } from 'react-colorful';
import Image from 'remix-image';
import { ColorDisplay, cartWidth } from '~/components/Cart';
import Logo from '~/components/Logo';
import { UserMenu } from '~/components/Navbar';
import { CartButton } from '~/components/Navbar/common';
import useHeader from '~/components/hooks/useHeader';
import Breadcrumb from '~/components/shared/Breadcrumb';
import CircularProgress from '~/components/shared/CircularProgress';
import { RemixLink } from '~/components/shared/links';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { Canvases, TDesignLab } from '~/routes/design-lab';
import type { Color } from '~/services/api-utils.server';
import type {
  Cart,
  ItemAttributes,
  PackBuilderPricesResponse,
  PackItem,
  PackProduct,
  Product,
} from '~/services/api.server';
import { getEmptyArray, getFormattedPrice, isCustomPackOrProduct, isFetching, isPackProduct } from '~/utils/common';
import { baseImgUrl } from '~/utils/constants';
import { useAuth } from '~/utils/contexts';
import type { MediaListProduct } from '~/utils/designLab';
import DraggableCarousel from '../Shop/DraggableCarousel';
import AnnounceKitComponent from '../Navbar/AnnounceKitComponent';

export const deleteIcon = (hex: string = 'C70039') => {
  return `data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 0 24 24' width='24px' fill='%23${hex} '%3e%3cpath d='M0 0h24v24H0V0z' fill='none'/%3e%3cpath d='M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z'/%3e%3c/svg%3e`;
};

export const rotateIcon = (hex: string = 'C70039') => {
  return `data:image/svg+xml,%3csvg width='50' height='50' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M6.26118 1.71814C5.67563 1.13258 4.87201 0.769135 3.97954 0.769135C2.19461 0.769135 0.75293 2.21485 0.75293 3.99978C0.75293 5.78471 2.19461 7.23043 3.97954 7.23043C5.48583 7.23043 6.74174 6.20066 7.10115 4.80744H6.26118C5.93004 5.74837 5.03354 6.42277 3.97954 6.42277C2.64286 6.42277 1.55655 5.33646 1.55655 3.99978C1.55655 2.6631 2.64286 1.5768 3.97954 1.5768C4.6499 1.5768 5.24757 1.85544 5.68371 2.29562L4.38337 3.59595H7.21019V0.769135L6.26118 1.71814Z' fill='%23${hex}'/%3e%3c/svg%3e`;
};

export const resizeIcon = (hex: string = 'C70039') => {
  return `data:image/svg+xml,%3csvg width='50' height='50' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M5.19167 0.679413L6.12049 1.60822L4.95341 2.76722L5.52685 3.34066L6.68585 2.17359L7.61466 3.1024V0.679413H5.19167ZM0.345703 3.1024L1.27451 2.17359L2.43351 3.34066L3.00695 2.76722L1.83988 1.60822L2.76869 0.679413H0.345703V3.1024ZM2.76869 7.94837L1.83988 7.01956L3.00695 5.86056L2.43351 5.28712L1.27451 6.4542L0.345703 5.52538V7.94837H2.76869ZM7.61466 5.52538L6.68585 6.4542L5.52685 5.28712L4.95341 5.86056L6.12049 7.01956L5.19167 7.94837H7.61466V5.52538Z' fill='%23${hex}'/%3e%3c/svg%3e`;
};


export const baseDesignLabUrl = baseImgUrl + 'swagcharm-design-lab/';

const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

const fontFamilies = ['Times New Roman', 'Arial', 'Verdana', 'Georgia', 'Courier', 'Comic Sans MS', 'Impact'];

export const DesignToggleButtonGroup = styled(MuiToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none',
    margin: theme.spacing(1),
    '&:not(:first-of-type)': {
      borderRadius: 8,
      border: 'none',
    },
    '&:first-of-type': {
      borderRadius: 8,
      border: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#FFF',
      color: '#EBEBEB',
    },
  },
}));

export const DesignToggleButton = styled(MuiToggleButton)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-evenly',
  fontSize: '12px',
  border: 'none',
  height: '75px',
  width: '75px',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    '&:hover': { backgroundColor: theme.palette.primary.main },
  },
}));

export const DesignMenuButton = styled(MuiButton)(({ theme }) => ({
  display: 'flex',
  marginBottom: '8px',
  flexDirection: 'column',
  justifyContent: 'space-evenly',
  fontSize: '12px',
  border: 'none',
  height: '75px !important',
  width: '75px !important',
  color: '#12123d8a',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    '&:hover': { backgroundColor: theme.palette.primary.main },
  },
}));

export const TextField = styled(MuiTextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFF',
    fontSize: 12,
    borderRadius: 8,
    '& fieldset': {
      borderColor: '#FFF',
    },
    '&:hover fieldset': {
      borderColor: '#FFF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#FFF',
    },
    '&.Mui-disabled fieldset': {
      borderColor: '#FFF',
    },
  },
});

export const TextToggleButtonGroup = styled(MuiToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButtonGroup-grouped': {
    margin: theme.spacing(0.5),
    border: 0,
    '&.Mui-disabled': {
      border: 0,
    },
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

export const Select = styled(MuiSelect)({
  '& .MuiSelect-select': {
    fontSize: 12,
    padding: '13px 14px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: '#EBEBEB',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#EBEBEB',
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: '#EBEBEB',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#EBEBEB',
  },
}) as unknown as typeof MuiSelect;

export const MenuItem = styled(MuiMenuItem)({
  fontSize: 12,
  padding: 8,
});

const StyledZoomButtonGroup = styled(MuiButtonGroup)(({ theme }) => ({
  backgroundColor: '#FFF',
  borderRadius: 8,
  padding: 1,
  height: 'fit-content',
  '& .MuiButtonGroup-grouped': {
    '&:not(:first-of-type)': {
      border: '1px solid #FFF',
      borderLeft: '1px solid #E8E8E8',
    },
    '&:first-of-type': {
      border: '1px solid #FFF',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.action.active,
    },
  },
}));

export const StyledFormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  '& .MuiTypography-root': {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}));

type SwitchProps = {
  checked: boolean;
  label: string;
  labelPlacement: FormControlLabelProps['labelPlacement'];
  handleOnChange: (value: boolean) => void;
};
export function Switch({ checked, label, labelPlacement, handleOnChange }: SwitchProps) {
  return (
    <StyledFormControlLabel
      control={<MuiSwitch checked={checked} onChange={(e, value) => handleOnChange(value)} />}
      label={label}
      labelPlacement={labelPlacement}
    />
  );
}

export const ControlButton = styled(MuiButton)({ '&:hover': { background: '#F6F6F7' } });

type ControlButtonGroupProps = {
  isCanvas3D: boolean;
  isRotating: boolean;
  onRotateModel: () => void;
  onChangeZoom: (value: boolean) => void;
  onResetCanvas: () => void;
};
export function ControlButtonGroup({
  isCanvas3D,
  isRotating,
  onRotateModel,
  onChangeZoom,
  onResetCanvas,
}: ControlButtonGroupProps) {
  const hoverProps = {
    bgcolor: isRotating ? 'primary.light' : '#FFF',
    '&:hover': { bgcolor: isRotating ? 'primary.main' : '#F6F6F7' },
    '& .MuiSvgIcon-root': {
      color: `${isRotating ? '#FFF' : 'action.active'} !important`,
    },
  };

  return (
    <StyledZoomButtonGroup>
      {isCanvas3D ? (
        <>
          <MuiTooltip title="Zoom In" arrow placement="top">
            <ControlButton size="small" onClick={() => onChangeZoom(true)}>
              <ZoomInIcon />
            </ControlButton>
          </MuiTooltip>
          <MuiTooltip title="Zoom Out" arrow placement="top">
            <ControlButton size="small" onClick={() => onChangeZoom(false)}>
              <ZoomOutIcon />
            </ControlButton>
          </MuiTooltip>
          <MuiTooltip title={isRotating ? 'Stop Rotation' : 'Rotate'} arrow placement="top">
            <ControlButton size="small" onClick={onRotateModel} sx={hoverProps}>
              <ThreeSixtyIcon />
            </ControlButton>
          </MuiTooltip>
        </>
      ) : (
        <MuiTooltip title="Reset All" arrow placement="top">
          <ControlButton size="small" onClick={onResetCanvas}>
            <RefreshIcon />
          </ControlButton>
        </MuiTooltip>
      )}
    </StyledZoomButtonGroup>
  );
}

const FormatColorFillIcon = ({ color }: { color: string }) => (
  <SvgIcon sx={{ color: 'text.secondary', width: 20, height: 20 }}>
    <path d="M16.56,8.94L7.62,0L6.21,1.41l2.38,2.38L3.44,8.94c-0.59,0.59-0.59,1.54,0,2.12l5.5,5.5C9.23,16.85,9.62,17,10,17 s0.77-0.15,1.06-0.44l5.5-5.5C17.15,10.48,17.15,9.53,16.56,8.94z M5.21,10L10,5.21L14.79,10H5.21z M19,11.5c0,0-2,2.17-2,3.5 c0,1.1,0.9,2,2,2s2-0.9,2-2C21,13.67,19,11.5,19,11.5z" />
    <path d="M2,20h20v4H2V20z" fill={color} />
  </SvgIcon>
);

const TShirtIcon = ({ color }: { color: string }) => (
  <SvgIcon sx={{ color, width: 20, height: 20 }}>
    <path d="M14.9999 18H6.99992C6.73471 18 6.48035 17.8946 6.29282 17.7071C6.10528 17.5196 5.99992 17.2652 5.99992 17V9.07L4.69992 10.12C4.30992 10.5 3.67992 10.5 3.28992 10.12L0.459925 7.29C0.367221 7.19749 0.293673 7.0876 0.243492 6.96662C0.193311 6.84565 0.16748 6.71597 0.16748 6.585C0.16748 6.45403 0.193311 6.32435 0.243492 6.20338C0.293673 6.0824 0.367221 5.97251 0.459925 5.88L6.33992 0H7.99992C7.99992 1.1 9.33992 2 10.9999 2C12.6599 2 13.9999 1.1 13.9999 0H15.6599L21.5399 5.88C21.9299 6.27 21.9299 6.9 21.5399 7.29L18.7099 10.12C18.3199 10.5 17.6899 10.5 17.2999 10.12L15.9999 9.07V17C15.9999 17.2652 15.8946 17.5196 15.707 17.7071C15.5195 17.8946 15.2651 18 14.9999 18ZM19.4199 6.58L15.1099 2.28C14.7999 2.63 14.4299 2.94 13.9999 3.2C13.1599 3.7 12.1299 4 10.9999 4C9.29992 4 7.78992 3.32 6.88992 2.28L2.57992 6.58L3.99992 8L6.99992 6H7.99992V16H13.9999V6H14.9999L17.9999 8L19.4199 6.58Z" />
  </SvgIcon>
);

type ColorPickerButtonProps = {
  fontColor: string;
  disabled: boolean;
  onColorChange: (color: string) => void;
};
function ColorPickerButton({ fontColor, disabled, onColorChange }: ColorPickerButtonProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  return (
    <div style={{ display: 'flex' }}>
      <MuiButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={disabled}
        disableTouchRipple
        onClick={handleClick}
      >
        <FormatColorFillIcon color={fontColor} />
        <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </MuiButton>
      <MuiMenu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 3, borderRadius: 2, '& .MuiMenu-list': { padding: 0 } }}
      >
        <MenuItem
          disableRipple
          sx={{
            p: 2,
            '.react-colorful__pointer': {
              width: 16,
              height: 16,
            },
            '.react-colorful__hue': {
              height: 12,
            },
            '.react-colorful__last-control': {
              borderRadius: 1,
              mt: 2,
            },
            '.react-colorful__saturation': {
              borderRadius: 1,
              borderBottom: 'unset',
            },
          }}
        >
          <HexColorPicker color={fontColor} onChange={onColorChange} />
        </MenuItem>
      </MuiMenu>
    </div>
  );
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#EDF7ED',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    padding: theme.spacing(1),
    color: theme.palette.success.dark,
    backgroundColor: '#EDF7ED',
    marginBottom: '0px !important',
  },
}));

const ReplyOutlinedIcon = ({ color }: { color: string }) => (
  <SvgIcon sx={{ color, width: 20, height: 18 }}>
    <path
      d="M19.707 8.29279L11.707 0.292787C11.5671 0.152977 11.389 0.057771 11.195 0.0192035C11.0011 -0.0193641 10.8 0.000438951 10.6173 0.076109C10.4346 0.151779 10.2785 0.279918 10.1686 0.444328C10.0587 0.608738 10 0.802037 10 0.999787V4.54479C7.26829 4.79758 4.72923 6.06065 2.87969 8.08681C1.03015 10.113 0.00327173 12.7564 0 15.4998V16.9998C0.000157856 17.2074 0.0649097 17.4098 0.185274 17.5789C0.305639 17.748 0.475649 17.8755 0.671726 17.9436C0.867802 18.0118 1.08022 18.0172 1.27953 17.9592C1.47883 17.9011 1.65514 17.7825 1.784 17.6198C2.7637 16.4547 3.96576 15.4966 5.31994 14.8014C6.67413 14.1062 8.15329 13.6878 9.671 13.5708C9.721 13.5648 9.846 13.5548 10 13.5448V16.9998C10 17.1975 10.0587 17.3908 10.1686 17.5552C10.2785 17.7197 10.4346 17.8478 10.6173 17.9235C10.8 17.9991 11.0011 18.0189 11.195 17.9804C11.389 17.9418 11.5671 17.8466 11.707 17.7068L19.707 9.70679C19.8945 9.51926 19.9998 9.26495 19.9998 8.99979C19.9998 8.73462 19.8945 8.48032 19.707 8.29279ZM12 14.5858V12.4998C12 12.2346 11.8946 11.9802 11.7071 11.7927C11.5196 11.6051 11.2652 11.4998 11 11.4998C10.745 11.4998 9.704 11.5498 9.438 11.5848C6.74286 11.8331 4.17742 12.8571 2.052 14.5328C2.29324 12.3272 3.33954 10.2881 4.99055 8.80595C6.64157 7.32376 8.78128 6.50263 11 6.49979C11.2652 6.49979 11.5196 6.39443 11.7071 6.20689C11.8946 6.01936 12 5.765 12 5.49979V3.41379L17.586 8.99979L12 14.5858Z"
      fill="black"
      fillOpacity="0.54"
    />
  </SvgIcon>
);

type ToggleDesignTypeProps = {
  designType: number;
  onChangeDesignType: (value: number) => void;
  onExportImageTextures: () => void;
  deactivate: boolean;
};
export function ToggleDesignType({
  designType,
  onChangeDesignType,
  onExportImageTextures,
  deactivate,
}: ToggleDesignTypeProps) {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [copied, setCopied] = React.useState(false);

  function fallbackCopyTextToClipboard(text: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    document.execCommand('copy');
    setCopied(true);
    document.body.removeChild(textArea);
  }
  function copyToClipboard(text: string) {
    if (!navigator.clipboard) {
      fallbackCopyTextToClipboard(text);
      return;
    }
    navigator.clipboard.writeText(text).then(() => setCopied(true));
  }

  React.useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  return (
    <>
      <DesignToggleButtonGroup
        value={designType}
        orientation={isMobile ? 'horizontal' : 'vertical'}
        exclusive
        onChange={deactivate ? undefined : (e, value) => onChangeDesignType(value)}
        sx={{
          '& > .MuiButtonBase-root': {
            width: { md: '55px', lg: '65px', xl: '75px' },
            height: { md: '55px', lg: '65px', xl: '75px' },
          },
        }}
      >
        <DesignToggleButton value={0} aria-label="Artwork">
          <DesignServicesOutlinedIcon fontSize="small" />
          Artwork
        </DesignToggleButton>
        <DesignToggleButton value={1} aria-label="Text">
          <FormatColorTextOutlinedIcon fontSize="small" />
          Text
        </DesignToggleButton>
        <DesignToggleButton value={2} aria-label="Product">
          <TShirtIcon color={designType === 2 ? '#FFF' : '#7C7B92'} />
          Product
        </DesignToggleButton>
      </DesignToggleButtonGroup>

      {isMobile ? (
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 2 }} />
      ) : (
        <Divider sx={{ width: '100%', my: 2 }} />
      )}

      <CustomTooltip open={copied} title="Copied!" placement="top" arrow enterTouchDelay={0}>
        <span>
          <DesignMenuButton
            sx={{ mt: { xs: 1, md: 0 } }}
            aria-label="Share"
            onClick={deactivate ? undefined : () => copyToClipboard(window.location.href)}
          >
            <ReplyOutlinedIcon color="#7C7B92" />
            Share
          </DesignMenuButton>
        </span>
      </CustomTooltip>
      <DesignMenuButton
        sx={{ mt: { xs: 1, md: 0 } }}
        aria-label="Download"
        onClick={deactivate ? undefined : onExportImageTextures}
      >
        <DownloadOutlinedIcon fontSize="small" />
        Download
      </DesignMenuButton>
    </>
  );
}

type TextStyleProps = {
  color: string;
  disabled: boolean;
  onFontWeightChange: (value: number) => void;
  onFontStyleChange: (value: string) => void;
  onFontUnderlineChange: (value: boolean) => void;
  onFontColorChange: (value: string) => void;
  onAlignmentChange: (value: string) => void;
  setShowArcMenu: () => void;
  selectedTextBox?: fabric.Text;
};
export function TextStyle({
  color,
  disabled,
  onFontWeightChange,
  onFontStyleChange,
  onFontUnderlineChange,
  onFontColorChange,
  onAlignmentChange,
  setShowArcMenu,
  selectedTextBox,
}: TextStyleProps) {
  const [formats, setFormats] = React.useState(() => ['']);
  const alignments = ['right', 'left', 'justify', 'center'];

  const handleChangeFormat = (newFormats: string[]) => {
    if (newFormats[newFormats.length - 1] === 'arc-text') return;

    const prunedNewFormats = pruneFormats(newFormats);
    const textAlignment = prunedNewFormats.find((align) => alignments.includes(align));

    setFormats(prunedNewFormats);

    if (prunedNewFormats.includes('bold')) onFontWeightChange(700);
    else onFontWeightChange(400);

    if (prunedNewFormats.includes('italic')) onFontStyleChange('italic');
    else onFontStyleChange('normal');

    if (prunedNewFormats.includes('underlined')) onFontUnderlineChange(true);
    else onFontUnderlineChange(false);

    if (textAlignment) onAlignmentChange(textAlignment);
    else onAlignmentChange('left');
  };

  const pruneFormats = (newFormats: string[]) => {
    const prunedFormat: string[] = [];
    let alignment = '';

    newFormats.forEach((format) => {
      if (format !== 'center' && format !== 'justify' && format !== 'left' && format !== 'right') {
        prunedFormat.push(format);
      } else {
        alignment = format;
      }
    });
    alignment && prunedFormat.push(alignment);

    return prunedFormat;
  };

  return (
    <div>
      <Typography variant="body2" fontWeight={500} color="text.secondary" mt={3} mb={1}>
        Text style
      </Typography>
      <TextToggleButtonGroup
        size="small"
        value={formats}
        disabled={disabled}
        onChange={(e, value) => handleChangeFormat(value)}
        sx={{ marginBottom: '8px', flexWrap: 'wrap' }}
      >
        <MuiToggleButton value="center" aria-label="center">
          <FormatAlignCenterOutlinedIcon fontSize="small" />
        </MuiToggleButton>
        <MuiToggleButton value="justify" aria-label="justify">
          <FormatAlignJustifyOutlinedIcon fontSize="small" />
        </MuiToggleButton>
        <MuiToggleButton value="left" aria-label="left">
          <FormatAlignLeftOutlinedIcon fontSize="small" />
        </MuiToggleButton>
        <MuiToggleButton style={{ marginRight: '16px' }} value="right" aria-label="right">
          <FormatAlignRightOutlinedIcon fontSize="small" />
        </MuiToggleButton>

        <MuiToggleButton value="bold" aria-label="bold">
          <FormatBoldIcon fontSize="small" />
        </MuiToggleButton>
        <MuiToggleButton value="italic" aria-label="italic">
          <FormatItalicIcon fontSize="small" />
        </MuiToggleButton>
        <MuiToggleButton value="underlined" aria-label="underlined">
          <FormatUnderlinedIcon fontSize="small" />
        </MuiToggleButton>
        <ColorPickerButton fontColor={color} disabled={disabled} onColorChange={onFontColorChange} />

        <MuiToggleButton
          disabled={!selectedTextBox}
          onClick={() => setShowArcMenu()}
          value="arc-text"
          aria-label="arc text"
        >
          <LooksOutlinedIcon fontSize="small" />
        </MuiToggleButton>
      </TextToggleButtonGroup>
    </div>
  );
}

type InsertTextProps = {
  text: string;
  onTextChange: (value: string) => void;
};
export function InsertText({ text, onTextChange }: InsertTextProps) {
  return (
    <>
      <Typography variant="body2" fontWeight={500} color="text.secondary" mb={1.5}>
        Insert your text here
      </Typography>
      <TextField
        id="add-text-field"
        multiline
        rows={4}
        placeholder="Lorem ipsum dolor sit amet"
        fullWidth
        value={text}
        onChange={(e) => onTextChange(e.currentTarget.value)}
        sx={(theme) => ({
          [theme.breakpoints.down('md')]: {
            border: '1px #EBEBEB solid',
            borderRadius: 2,
          },
        })}
      />
    </>
  );
}

type TextFontProp = {
  fontSize: number;
  fontFamily: string;
  showArcMenu: boolean;
  onFontSizeChange: (value: number) => void;
  onFontFamilyChange: (value: string) => void;
  onDiameterChange: (value: number, clockwise: boolean) => void;
  onRemoveTextPath: () => void;
  onKerningChange: (value: number) => void;
  textLength?: number;
  text?: string;
  flip?: boolean;
  diameter?: number;
  kerning?: number;
};

export function TextFont({
  fontSize,
  fontFamily,
  showArcMenu,
  onFontSizeChange,
  onFontFamilyChange,
  onDiameterChange,
  onRemoveTextPath,
  onKerningChange,
  textLength,
  flip,
  diameter,
  kerning,
}: TextFontProp) {
  const handleDiameterChange = (event: Event, newValue: number | number[]) => {
    if ((newValue as number) === (textLength ? textLength * 3 : 1)) {
      onRemoveTextPath();
    } else {
      onDiameterChange(newValue as number, flip ?? true);
    }
  };

  const handleKerningChange = (event: Event, newValue: number | number[]) => {
    onKerningChange(newValue as number);
  };

  return (
    <Grid container justifyContent="space-between" spacing={3}>
      <Grid item xs={8} md={12} xl={7}>
        <FormControl size="small" fullWidth sx={{ bgcolor: '#FFF', borderRadius: '6px', height: 49 }}>
          <Select
            value={fontFamily}
            IconComponent={KeyboardArrowDownOutlinedIcon}
            onChange={(e) => onFontFamilyChange(e.target.value)}
          >
            {fontFamilies.map((fontFamily) => (
              <MenuItem key={fontFamily} value={fontFamily}>
                {fontFamily}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={4} md={12} xl={5}>
        <FormControl size="small" fullWidth sx={{ bgcolor: '#FFF', borderRadius: '6px', height: 49 }}>
          <Select
            value={`${fontSize}`}
            IconComponent={KeyboardArrowDownOutlinedIcon}
            onChange={(e) => onFontSizeChange(+e.target.value)}
          >
            {fontSizes.map((fontSize) => (
              <MenuItem key={fontSize} value={fontSize}>
                {fontSize} pt
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {showArcMenu && textLength && (
        <Grid item width="75%">
          <Stack spacing={2}>
            <Typography variant="body2" fontWeight={500} color="text.secondary" mb={1}>
              Arc Text
            </Typography>
            <Stack flexDirection="row" alignItems="end">
              <Typography variant="body2" fontWeight={500} color="text.secondary" mb={1}>
                Diameter:
              </Typography>
              <Slider
                value={diameter ?? textLength * 3}
                size="medium"
                valueLabelDisplay="off"
                disabled={!textLength}
                min={textLength ? textLength * 3 : 1}
                max={textLength ? textLength * 10 : 3}
                onChange={handleDiameterChange}
                sx={{ ml: 2 }}
              />
            </Stack>
            <Stack flexDirection="row" alignItems="end">
              <Typography variant="body2" fontWeight={500} color="text.secondary" mb={1}>
                Kerning:
              </Typography>
              <Slider
                value={kerning ?? 0}
                size="medium"
                disabled={!textLength}
                valueLabelDisplay="off"
                min={-500}
                max={500}
                onChange={handleKerningChange}
                sx={{ ml: 3 }}
              />
            </Stack>
          </Stack>
          <Stack flexDirection="row" alignItems="end" mt={1}>
            <Typography variant="body2" fontWeight={500} color="text.secondary" mb={1}>
              Flip:
            </Typography>
            <Checkbox
              checked={flip !== undefined ? !flip : false}
              disabled={!textLength ? true : !diameter}
              sx={{ ml: 5 }}
              color="primary"
              onChange={() => {
                onDiameterChange(diameter!, !flip);
              }}
            />
          </Stack>
        </Grid>
      )}
    </Grid>
  );
}

type ImgCarouselProps = {
  currentPage: number;
  product: Product | PackProduct;
  mediaList: MediaListProduct[];
  setCurrentPage: (value: number) => void;
  productSlug: string;
};

const productsPerPage = 5;

export function ItemsCarousel({ currentPage, product, mediaList, setCurrentPage, productSlug }: ImgCarouselProps) {
  const theme = useTheme();
  const { designLab } = useLoaderData<{ designLab: TDesignLab }>();

  const isSummaryView = productSlug === 'summary';

  const fetchers = useFetchers();
  const colorFetcher = fetchers.find((f) => f.submission?.formData.get('_action') === 'changeProductColor');
  const productColorChanging = +(colorFetcher?.submission?.formData?.get('productId') ?? 0);

  const showArrowUp = currentPage !== 1;
  const isPack = isPackProduct(product);
  const hideArrowDown = isPack
    ? product.filler
      ? product.items.length + 1 <= currentPage * productsPerPage - 1
      : product.items.length + 1 <= currentPage * productsPerPage
    : true;

  const onChangePage = (direction: boolean) => {
    if (direction) {
      setCurrentPage(currentPage + 1);
    } else {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <>
      {!isSummaryView && (
        <Typography display={{ xs: 'none', md: 'flex' }} variant="subtitle1" color="#000000" fontWeight={600} mb={1.5}>
          {isPack ? 'Pack Items' : 'Item'}
        </Typography>
      )}
      <Grid
        container
        direction={{ xs: 'row', md: 'column' }}
        alignItems="center"
        rowGap={{ md: 1 }}
        columnGap={{ xs: 1, md: 0 }}
        px={{ xs: 2, md: 0 }}
        justifyContent={{ xs: 'start', md: 'initial' }}
        flexWrap={{ xs: 'nowrap', md: 'wrap' }}
      >
        {showArrowUp && !isSummaryView && (
          <IconButton
            size="large"
            color="inherit"
            aria-label="user menu"
            aria-haspopup="true"
            disableRipple
            disabled={currentPage === 1}
            onClick={() => onChangePage(false)}
            sx={{ p: 0, color: '#667080', '&:hover': { color: '#000000' } }}
          >
            <KeyboardArrowUpOutlinedIcon />
          </IconButton>
        )}
        {isSummaryView ? (
          <Stack
            sx={{
              borderRadius: 1,
              border: `1px solid ${theme.palette.primary.main}`,
              bgcolor: 'rgba(0, 0, 0, 0.04)',
              p: 1,
              height: '100%',
              '&:hover': { border: `1px solid ${theme.palette.primary.main}` },
            }}
          >
            <Image
              src={
                designLab.generated_images?.[designLab?.product.slug] ??
                (designLab?.product?.blank_image || designLab?.product?.image)
              }
              alt="Summary Image Carousel"
              responsive={[{ size: { width: 64, height: 64 } }]}
              options={{ fit: 'inside' }}
              dprVariants={[1, 3]}
              loading="lazy"
              style={{ width: 64, height: 64 }}
            />
          </Stack>
        ) : (
          mediaList?.map((p) => {
            const { currentItemProduct } = getItemOrProduct(p.id, product);
            const canBeDesigned = currentItemProduct?.design_lab_ready && !isCustomPackOrProduct(currentItemProduct);
            const isColorChanging = productColorChanging === p.id;
            return (
              <Grid key={p.id} item>
                <MuiTooltip title={!canBeDesigned ? "This product can't be designed" : ''} arrow placement="right">
                  <span>
                    <MuiButton
                      component={RemixLink}
                      href={`/design-lab/${isPack ? `${product.slug}/items/${p.slug}` : `bulk/${p.slug}`}?id=${
                        p.color
                      }`}
                      replace
                      disabled={!canBeDesigned || isSummaryView}
                      sx={{
                        borderRadius: 1,
                        border:
                          productSlug === p.slug ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        p: 1,
                        height: '100%',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                        },
                      }}
                    >
                      <Image
                        key={p.id}
                        src={p?.image}
                        alt={p.name}
                        responsive={[{ size: { width: 64, height: 64 } }]}
                        options={{ fit: 'inside' }}
                        dprVariants={[1, 3]}
                        loading="lazy"
                        style={{ width: 64, height: 64, opacity: isColorChanging ? 0.3 : 1 }}
                      />
                      {isColorChanging && <MuiCircularProgress size={25} sx={{ position: 'absolute' }} />}
                    </MuiButton>
                  </span>
                </MuiTooltip>
              </Grid>
            );
          })
        )}

        {!hideArrowDown && !isSummaryView && (
          <IconButton
            size="large"
            color="inherit"
            aria-label="user menu"
            aria-haspopup="true"
            disableRipple
            onClick={() => onChangePage(true)}
            sx={{ p: 0, color: '#667080', '&:hover': { color: '#000000' } }}
          >
            <KeyboardArrowDownOutlinedIcon />
          </IconButton>
        )}
      </Grid>
    </>
  );
}

export function Navbar({
  cart,
  product,
  canvases,
  lastPageComesFrom,
  fromPackBuilder,
  setOpenCart,
  productSlug,
}: {
  cart: Cart;
  product: Product;
  canvases: Canvases;
  lastPageComesFrom: string;
  fromPackBuilder: boolean;
  setOpenCart: React.Dispatch<React.SetStateAction<boolean>>;
  productSlug: string;
}) {
  const submit = useSubmit();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const profile = useAuth();

  const { designLab } = useLoaderData<{ designLab: TDesignLab }>();
  const isSummaryView = productSlug === 'summary';

  const handleNext = async () => {
    submit({ _action: 'gotToSummary', locationsInfo: JSON.stringify(canvases) }, { method: 'post' });
  };

  const breadcrumbEntries = [
    { name: 'Home', href: '/' },
    designLab.last_page_comes_from.includes('build-pack')
      ? { name: 'Pack Builder', href: '/guided-pack-builder' }
      : { name: 'Shop', href: '/shop' },
    {
      name: product.name || '',
      href: product.id === undefined || product.id < 0 ? '' : '/shop/' + product.slug,
      noLink: fromPackBuilder,
    },
  ];

  const { currentItemProduct } = isSummaryView ? {} : getItemOrProduct(productSlug, product);
  const packBreadcrumbEntries = [
    ...breadcrumbEntries,
    {
      name: currentItemProduct?.name || '',
      href: product.id === undefined || product.id < 0 ? '' : '/shop/' + productSlug,
    },
  ];

  const to = lastPageComesFrom?.includes('design-lab') ? '/shop' : lastPageComesFrom;
  const backButtonProps = designLab.created
    ? { LinkComponent: RemixLink, href: `/design-lab/${isPackProduct(product) ? '' : 'bulk/'}${product.slug}` }
    : { onClick: goBack };
  return (
    <NavbarWrapper>
      <Grid
        item
        container
        xs={7}
        alignItems="center"
        justifyContent="flex-start"
        pl={2}
        display={{ xs: 'none', md: 'block' }}
      >
        <Typography variant="h5" color="#000000" fontWeight={600}>
          Design Lab
        </Typography>
        <Breadcrumb entries={isPackProduct(product) && !isSummaryView ? packBreadcrumbEntries : breadcrumbEntries} />
      </Grid>
      <Grid item container xs={5} alignItems="center" justifyContent="flex-end" flexWrap={{ xs: 'nowrap', md: 'wrap' }}>
        <MuiButton
          {...backButtonProps}
          disabled={navigation.state !== 'idle'}
          variant="outlined"
          startIcon={<ChevronLeftIcon />}
          sx={{
            textTransform: 'uppercase',
            mr: isSummaryView ? 2 : 1,
            display: { xs: 'none', md: 'flex' },
          }}
        >
          Back
        </MuiButton>
        {!isSummaryView && (
          <MuiButton
            onClick={handleNext}
            disabled={navigation.state !== 'idle'}
            variant="contained"
            disableRipple
            endIcon={<ChevronRightIcon />}
            sx={{
              bgcolor: 'primary.main',
              textTransform: 'uppercase',
              boxShadow: 'none',
              mr: 2,
              '&:hover': { boxShadow: 'none' },
              display: { xs: 'none', md: 'flex' },
            }}
          >
            Next
          </MuiButton>
        )}
        {profile && <UserMenu />}
        <AnnounceKitComponent />
        <CartButton totalProducts={cart.products.length} setOpenCart={setOpenCart} />
        <IconButton component={RemixLink} href={to} sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </Grid>
    </NavbarWrapper>
  );
}

export function NavbarEmptyQueue({
  cart,
  setOpenCart,
}: {
  cart: Cart;
  setOpenCart: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const breadcrumbEntries = [
    { name: 'Home', href: '/' },
    { name: 'Shop', href: '/shop' },
  ];
  const to = '/shop';
  const profile = useAuth();

  return (
    <NavbarWrapper>
      <Grid
        item
        container
        xs={7}
        alignItems="center"
        justifyContent="flex-start"
        pl={2}
        display={{ xs: 'none', md: 'block' }}
      >
        <Typography variant="h5" color="#000000" fontWeight={600}>
          Design Lab
        </Typography>
        <Breadcrumb entries={breadcrumbEntries} />
      </Grid>
      <Grid item container xs={5} alignItems="center" justifyContent="flex-end" flexWrap={{ xs: 'nowrap', md: 'wrap' }}>
        <MuiButton
          disabled={true}
          variant="contained"
          disableRipple
          endIcon={<ChevronRightIcon />}
          sx={{
            bgcolor: 'primary.main',
            textTransform: 'uppercase',
            boxShadow: 'none',
            mr: 2,
            '&:hover': { boxShadow: 'none' },
            display: { xs: 'none', md: 'flex' },
          }}
        >
          Next
        </MuiButton>
        {profile && <UserMenu />}
        <AnnounceKitComponent />
        <CartButton totalProducts={cart.products.length} setOpenCart={setOpenCart} />
        <IconButton component={RemixLink} href={to} sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </Grid>
    </NavbarWrapper>
  );
}

function NavbarWrapper({ children }: { children: JSX.Element[] }) {
  const [hasScroll] = useHeader();
  const shadow =
    '-4px 2px 4px -1px rgb(0 0 0 / 20%), -4px 4px 5px 0px rgb(0 0 0 / 14%), -8px 1px 10px 0px rgb(0 0 0 / 12%)';

  return (
    <AppBar
      position="sticky"
      elevation={4}
      sx={{ height: 72, bgcolor: '#FFF', boxShadow: hasScroll ? shadow : 'none' }}
    >
      <Toolbar
        sx={{
          height: '100%',
          border: 'none',
          borderBottom: '1px solid #EFEFEF',
          pl: { md: '0px !important' },
        }}
      >
        <Grid container alignItems="center" justifyContent={{ xs: 'space-between', md: 'inherit' }}>
          <Grid item xs={1} display={{ xs: 'flex', md: 'none' }} justifyContent="center" alignItems="center">
            <Logo isSmall />
          </Grid>
          {children}
        </Grid>
      </Toolbar>
    </AppBar>
  );
}

const StyledSelect = styled(MuiSelect)({
  lineHeight: '1.25rem',
  '& .MuiSelect-select': { padding: '0 20px 0 8px !important', fontSize: 12 },
  '& .MuiSelect-icon': { right: 0, fontSize: 20 },
  '& input': { padding: 0, margin: 0, border: 'none' },
});

type ItemPricePopoverProps = {
  currentItemProduct: Product;
  isFillerOrContainer: boolean;
  prices: PackBuilderPricesResponse;
  openCart: boolean;
  bottomBarExpanded: boolean;
};
export function ItemPricePopover({
  currentItemProduct,
  isFillerOrContainer,
  prices,
  openCart,
  bottomBarExpanded,
}: ItemPricePopoverProps) {
  const changeColorFetcher = useFetcher();
  const changeUnitPerPackFetcher = useFetcher();

  const fetchers = useFetchers();
  const isFetchingPrice = isFetching(fetchers, ['changeTotalQty', 'changeColorQuantity', 'changeItemUnitsPerPack']);

  const pricesProduct = prices.products.find((p) => p.product === currentItemProduct.id);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [noColors, setNoColors] = React.useState(pricesProduct?.no_colors || 1);
  const [itemUnitsPerPack, setItemUnitsPerPack] = React.useState(pricesProduct?.units_per_pack || 1);

  React.useEffect(() => {
    const pricesP = prices.products.find((p) => p.product === currentItemProduct.id);
    setNoColors(pricesP?.no_colors || 1);
    setItemUnitsPerPack(pricesP?.units_per_pack || 1);
  }, [prices, currentItemProduct.id]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleChangeColorQuantity = (e: SelectChangeEvent<unknown>) => {
    setNoColors(+e.target.value);
    changeColorFetcher.submit(
      {
        _action: 'changeColorQuantity',
        itemProductId: currentItemProduct.id.toString(),
        noColor: e.target.value.toString(),
      },
      { method: 'post' }
    );
  };

  const handleChangeUnitPerPack = (e: SelectChangeEvent<unknown>) => {
    setItemUnitsPerPack(+e.target.value);
    changeUnitPerPackFetcher.submit(
      {
        _action: 'changeItemUnitsPerPack',
        itemProductId: currentItemProduct.id.toString(),
        itemUnitsPerPack: e.target.value.toString(),
      },
      { method: 'post' }
    );
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'prices-popover' : undefined;
  const isFetchingPriceStyle = [isFetchingPrice && { color: 'text.disabled' }];

  const totalPrice = pricesProduct?.prices.total.amount;
  const blankPrice = pricesProduct?.prices.blank.amount;
  const decorationPrice = pricesProduct?.prices.decoration.amount;
  const unitsPerPack = pricesProduct?.units_per_pack ?? 1;
  const totalQuantity = prices.pack_quantity;

  return (
    <Box position="fixed" right={openCart ? cartWidth + 20 : 20} bottom={bottomBarExpanded ? 193 : 90}>
      <Fab
        aria-describedby={popoverId}
        color="primary"
        onClick={handleClick}
        sx={{
          width: 'auto',
          height: 44,
          alignItems: 'center',
          borderRadius: 2,
          p: 1,
          bgcolor: '#FFF',
          '&:hover': { bgcolor: 'white' },
        }}
      >
        {!open && (
          <>
            <Typography variant="body1" fontWeight={500} color="text.secondary">
              Item Price:
            </Typography>
            <CircularProgress show={isFetchingPrice} size={12} containerProps={{ mx: 0.5 }} />
            <Typography
              variant="body2"
              component="span"
              fontWeight={500}
              color="#000000"
              display="inline"
              mr={0.5}
              sx={isFetchingPriceStyle}
            >
              {getFormattedPrice((totalPrice * totalQuantity) / 100)}
            </Typography>
          </>
        )}
        <KeyboardArrowUpOutlinedIcon
          sx={{
            color: 'primary.main',
            transition: 'transform 200ms ease',
            transform: open ? 'rotate(180deg)' : 'none',
            cursor: 'pointer !important',
          }}
        />
      </Fab>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: -8, horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box display="grid" p={2} borderRadius={2} minWidth={500} gap={1}>
          <Grid
            container
            alignItems="center"
            sx={{ bgcolor: '#F0F5F9', height: 36, px: 1.5, borderRadius: '8px 8px 0px 0px', mb: 1 }}
          >
            <Typography variant="body2" fontWeight={500} sx={{ color: '#12123D' }}>
              Price Breakdown
            </Typography>
          </Grid>
          <Stack px={1.5} gap={1}>
            <Grid container justifyContent="space-between">
              <Stack direction="row" alignItems="center">
                <Typography variant="body2" mr={0.5}>
                  Qty per pack:
                </Typography>
                {isFillerOrContainer ? (
                  <Typography variant="body2">1</Typography>
                ) : (
                  <StyledSelect value={itemUnitsPerPack} onChange={handleChangeUnitPerPack}>
                    {getEmptyArray(10).map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                )}
              </Stack>
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" mx={0.5} sx={isFetchingPriceStyle}>
                  Total packs:
                </Typography>
                <Typography variant="body2" sx={isFetchingPriceStyle}>
                  {totalQuantity}
                </Typography>
              </Stack>
            </Grid>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="body2">Blank product:</Typography>
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" ml={0.5} sx={isFetchingPriceStyle}>
                  {getFormattedPrice((blankPrice * unitsPerPack) / 100)}
                </Typography>
              </Stack>
            </Grid>
            <Grid container justifyContent="space-between" alignItems="center">
              <Stack direction="row">
                <Typography variant="body2" mr={0.5}>
                  Decoration with
                </Typography>
                <StyledSelect value={noColors} onChange={handleChangeColorQuantity}>
                  {getEmptyArray(10).map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <Typography variant="body2" ml={0.5}>
                  {`color${noColors > 1 ? 's' : ''}:`}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" ml={0.5} sx={isFetchingPriceStyle}>
                  {getFormattedPrice((decorationPrice * unitsPerPack) / 100)}
                </Typography>
              </Stack>
            </Grid>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="body2">Price per pack:</Typography>
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" ml={0.5} sx={isFetchingPriceStyle}>
                  {getFormattedPrice((totalPrice * unitsPerPack) / 100)}
                </Typography>
              </Stack>
            </Grid>
            <Divider />
            <Grid container justifyContent="end" alignItems="center">
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" fontWeight={500} mx={0.5} sx={isFetchingPriceStyle}>
                  Estimated Price:
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={isFetchingPriceStyle}>
                  {getFormattedPrice((totalPrice * unitsPerPack * totalQuantity) / 100)}
                </Typography>
              </Stack>
            </Grid>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}

type ProductPricePopoverProps = {
  currentItemProduct: Product;
  prices: PackBuilderPricesResponse;
  openCart: boolean;
  bottomBarExpanded: boolean;
};
export function ProductPricePopover({
  currentItemProduct,
  prices,
  openCart,
  bottomBarExpanded,
}: ProductPricePopoverProps) {
  const changeColorFetcher = useFetcher();
  const changeTotalQtyFetcher = useFetcher();

  const pricesProduct = prices.products.find((p) => p.product === currentItemProduct.id);

  const fetchers = useFetchers();
  const isFetchingPrice = isFetching(fetchers, ['changeTotalQty', 'changeColorQuantity', 'changeItemUnitsPerPack']);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [noColors, setNoColors] = React.useState(pricesProduct?.no_colors || 1);
  const [quantity, setQuantity] = React.useState<number | string>(pricesProduct.quantity || 100);

  React.useEffect(() => {
    const pricesP = prices.products.find((p) => p.product === currentItemProduct.id);
    setNoColors(pricesP?.no_colors || 1);
  }, [prices, currentItemProduct.id]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);

  const handleChangeColorQuantity = (e: SelectChangeEvent<unknown>) => {
    setNoColors(+e.target.value);
    changeColorFetcher.submit(
      {
        _action: 'changeColorQuantity',
        itemProductId: currentItemProduct.id.toString(),
        noColor: e.target.value.toString(),
      },
      { method: 'post' }
    );
  };

  const handleChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
    const qty = e.target.value.trim();
    const parsedQty = parseInt(qty, 10);
    if ((parsedQty === 0 || parsedQty > 5000) && qty !== '') return;
    setQuantity(qty ? parsedQty : '');
    if (parsedQty) {
      changeTotalQtyFetcher.submit(
        {
          _action: 'changeTotalQty',
          totalQuantity: parsedQty.toString(),
        },
        { method: 'post' }
      );
    }
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const popoverId = open ? 'simple-popover' : undefined;
  const isFetchingPriceStyle = [isFetchingPrice && { color: 'text.disabled' }];

  const totalPrice = pricesProduct?.prices.total.amount;
  const blankPrice = pricesProduct?.prices.blank.amount;
  const decorationPrice = pricesProduct?.prices.decoration.amount;
  const totalQuantity = pricesProduct?.quantity;

  return (
    <Box position="fixed" right={openCart ? cartWidth + 20 : 20} bottom={bottomBarExpanded ? 160 : 90}>
      <Fab
        aria-describedby={popoverId}
        color="primary"
        onClick={handleClick}
        sx={{
          width: 'auto',
          height: 44,
          alignItems: 'center',
          borderRadius: 2,
          p: 1,
          bgcolor: '#FFF',
          '&:hover': { bgcolor: 'white' },
        }}
      >
        {!open && (
          <>
            <Typography variant="body1" fontWeight={500} color="text.secondary">
              Item price:
            </Typography>
            <CircularProgress show={isFetchingPrice} size={12} containerProps={{ mx: 0.5 }} />
            <Typography
              variant="body2"
              component="span"
              fontWeight={500}
              color="#000000"
              display="inline"
              mr={0.5}
              sx={isFetchingPriceStyle}
            >
              {getFormattedPrice((totalPrice * totalQuantity) / 100)}
            </Typography>
          </>
        )}
        <KeyboardArrowUpOutlinedIcon
          sx={{
            color: 'primary.main',
            transition: 'transform 200ms ease',
            transform: open ? 'rotate(180deg)' : 'none',
            cursor: 'pointer !important',
          }}
        />
      </Fab>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: -8, horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box display="grid" p={2} borderRadius={2} minWidth={500} gap={1}>
          <Grid
            container
            alignItems="center"
            sx={{ bgcolor: '#F0F5F9', height: 36, px: 1.5, borderRadius: '8px 8px 0px 0px', mb: 1 }}
          >
            <Typography variant="body2" fontWeight={500} sx={{ color: '#12123D' }}>
              Price Breakdown
            </Typography>
          </Grid>
          <Stack px={1.5} gap={1}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="body2">Blank product:</Typography>
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" ml={0.5} sx={isFetchingPriceStyle}>
                  {getFormattedPrice(blankPrice / 100)}
                </Typography>
              </Stack>
            </Grid>
            <Grid container justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center">
                <Typography variant="body2" mr={0.5}>
                  Decoration with
                </Typography>
                <StyledSelect value={noColors} onChange={handleChangeColorQuantity}>
                  {getEmptyArray(10).map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <Typography variant="body2" ml={0.5}>
                  {`color${noColors > 1 ? 's' : ''}:`}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" ml={0.5} sx={isFetchingPriceStyle}>
                  {getFormattedPrice(decorationPrice / 100)}
                </Typography>
              </Stack>
            </Grid>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="body2">Estimated price:</Typography>
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" ml={0.5} sx={isFetchingPriceStyle}>
                  {getFormattedPrice(pricesProduct?.price.amount / 100)}
                </Typography>
              </Stack>
            </Grid>
            <Divider />
            <Grid container justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" fontWeight={500}>
                  Quantity
                </Typography>
                <MuiTextField
                  name="quantity"
                  id="outlined-basic"
                  variant="outlined"
                  size="small"
                  value={quantity}
                  type="number"
                  inputProps={{ min: 1, max: 5000 }}
                  onChange={handleChangeQuantity}
                  sx={{ maxWidth: '120px' }}
                />
              </Stack>
              <Stack direction="row" alignItems="center">
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" fontWeight={500} mx={0.5} sx={isFetchingPriceStyle}>
                  Estimated Total:
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={isFetchingPriceStyle}>
                  {getFormattedPrice((pricesProduct?.price.amount * pricesProduct?.quantity) / 100)}
                </Typography>
              </Stack>
            </Grid>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}

export function getItemOrProduct(id: number | string, product: Product) {
  if (!isPackProduct(product)) return { currentItemProduct: product, isFillerOrContainer: false };

  if (product.container.id === id || product.container.slug === id)
    return { currentItemProduct: product.container, isFillerOrContainer: true };
  if (product.filler?.id === id || product.filler?.slug === id)
    return { currentItemProduct: product.filler, isFillerOrContainer: true };
  const itemProduct = product.items.find((item) => item.product?.id === id || item.product?.slug === id);
  return { currentItemProduct: itemProduct?.product, isFillerOrContainer: false };
}

export function getInitialCanvases(product: Product) {
  const canvases: Canvases = {};
  if (isPackProduct(product)) {
    canvases[product.container.id] = {};
    if (product.filler) {
      canvases[product.filler.id!] = {};
    }
    for (const item of product.items) {
      canvases[item.product!.id!] = {};
    }
  } else {
    canvases[product.id!] = {};
  }

  return canvases;
}

export function deleteObject(eventData: MouseEvent, transform: Transform, x: number, y: number) {
  const { canvas } = transform.target;
  if (canvas) {
    const activeObjects = canvas.getActiveObjects();
    canvas.remove(...activeObjects);
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }
  return true;
}

export function CanvasButton({
  text,
  icon,
  disabled = false,
  onClick,
}: {
  text: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Button
      color="inherit"
      onClick={onClick}
      disabled={disabled}
      sx={{ flexDirection: 'column', alignItems: 'center', fontSize: 12, color: 'text.secondary', p: 0.5 }}
    >
      {icon} {text}
    </Button>
  );
}

type MenuItemProp = {
  text: string;
  onClick: () => void;
  icon: JSX.Element;
};

export function CanvasMenuButton({
  text,
  icon,
  disabled = false,
  menuItems,
}: {
  text: string;
  icon: React.ReactNode;
  disabled?: boolean;
  menuItems: Array<MenuItemProp>;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleMenuItemClick = (item: MenuItemProp) => {
    handleClose();
    item.onClick();
  };

  return (
    <div style={{ display: 'flex' }}>
      <MuiButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={disabled}
        disableTouchRipple
        onClick={handleClick}
        sx={{ flexDirection: 'column', alignItems: 'center', fontSize: 12, color: 'text.secondary' /* p: 0.5  */ }}
      >
        {icon}
        <Box flexDirection="row" display="flex" pl={0.625}>
          {text}
          <ArrowDropDownIcon fontSize="small" color="inherit" />
        </Box>
      </MuiButton>
      <MuiMenu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        sx={{ '& .MuiMenu-list': { padding: 0 } }}
      >
        {menuItems.map((item) => (
          <MenuItem key={item.text} onClick={() => handleMenuItemClick(item)}>
            <Stack
              flexDirection="row"
              gap={1}
              alignItems="center"
              justifyContent="center"
              color="text.secondary"
              fontSize={12}
            >
              {item.icon}
              {item.text}
            </Stack>
          </MenuItem>
        ))}
      </MuiMenu>
    </div>
  );
}

type ProductDetailsProps = {
  product: Product | PackProduct;
  isSelected: boolean;
  designLab: TDesignLab;
  colorId: string;
  closeDrawer: () => void;
};

export function ProductDetails({ product, isSelected, designLab, colorId, closeDrawer }: ProductDetailsProps) {
  const fetcher = useFetcher();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isPack = isPackProduct(product);
  const href = `/design-lab/${
    isPack ? `${product.slug}/items/5-x-7-heathered-bound-notebook?id=${colorId}` : `bulk/${product.slug}?id=${colorId}`
  }`;
  const submit = useSubmit();
  const onClickRedirect = () => {
    if (isSelected) return;
    submit(
      {
        href,
        colorId,
        _action: 'redirect-product-in-queue',
      },
      { method: 'post', replace: true }
    );
    closeDrawer();
  };

  return (
    <Box
      borderRadius={2}
      border={`1px solid ${isSelected ? '#009DC8' : '#EBEBEB'}`}
      onClick={onClickRedirect}
      sx={{
        cursor: 'pointer',
        '&  .MuiAccordionDetails-root': {
          pb: 0,
          maxWidth: 548,
        },
      }}
    >
      <Stack direction="row" px={{ xs: 2, md: 3 }} py={{ xs: 1, md: 2 }} position="relative">
        <Box
          position="absolute"
          top={0}
          left={0}
          py={{ xs: 0.75, md: 1 }}
          px={{ xs: 1, md: 1.5 }}
          color="primary.main"
          sx={{ backgroundColor: '#E1F5FE', borderRadius: '8px 0 8px 0' }}
        >
          <Typography variant="subtitle2" color="primary.main">
            {isPack ? 'Pack template' : 'Bulk'}
          </Typography>
        </Box>

        <Image
          src={designLab.generated_images[product.id] || product.blank_image || product.image || NoImageAvailable}
          key={designLab.generated_images[product.id] || product.blank_image || product.image}
          alt="Product"
          width={isMobile ? 84 : 95}
          height={isMobile ? 84 : 95}
          responsive={[{ size: { width: isMobile ? 84 : 95, height: isMobile ? 84 : 95 } }]}
          options={{ fit: 'inside' }}
          dprVariants={[1, 3]}
          loading="lazy"
        />
        <Stack width="100%" justifyContent="space-between">
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1.5} mb={{ xs: 3, md: 1 }}>
            <MuiTooltip title={product.name} placement="top" arrow sx={{ pointerEvents: 'auto !important' }}>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.primary',
                  '&:hover': { color: 'primary.main' },
                  fontWeight: 600,
                  overflow: 'hidden',
                  fontSize: { xs: 14, md: 20 },
                  maxWidth: { xs: 'calc(100vw - 185px)', md: '320px' },
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  textOverflow: 'ellipsis',
                }}
              >
                {product.name}
              </Typography>
            </MuiTooltip>

            <Stack direction="row" alignItems="center">
              <fetcher.Form
                method="post"
                action="/design-lab"
                style={{ marginLeft: '12px' }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <input type="hidden" name="productId" value={colorId} />
                <input type="hidden" name="_action" value="delete" />
                <input type="hidden" name="isSelected" value={isSelected ? 'true' : 'false'} />
                <IconButton type="submit" aria-label="delete" sx={{ p: 0 }}>
                  <DeleteOutlineIcon />
                </IconButton>
              </fetcher.Form>
            </Stack>
          </Stack>
          {!isPack && <ProductColor product={product} attributes={designLab.items_attributes} />}
          {isPack && (
            <Typography variant="subtitle1" color="text.primary" fontSize={{ xs: 12, md: 16 }} fontWeight={500}>
              Products: {product.items?.length}
            </Typography>
          )}
        </Stack>
      </Stack>
      {isPack && (
        <PackDetails>
          <PackItems products={product.items} productsAttributes={designLab.items_attributes} />
        </PackDetails>
      )}
    </Box>
  );
}

function ProductColor({ product, attributes }: { product: Product; attributes: ItemAttributes[] }) {
  const productAttribute = attributes.find((attribute) => attribute.product_id === product.id);
  const colorValue = productAttribute?.attributes?.find((attribute) => attribute?.name === 'color')?.value;
  const color = product.available_attributes['colors'].find((color) => color.name === colorValue);
  return (
    <Box
      sx={{
        '.MuiTypography-subtitle2': {
          fontSize: 16,
          color: 'text.primary',
        },
      }}
    >
      {color?.hex && <ColorDisplay color={color as Color} />}
    </Box>
  );
}

export function PackDetails({ children }: { children: JSX.Element | JSX.Element[] }) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Box
      border="1px solid #EBEBEB"
      overflow="hidden"
      sx={{ borderRadius: '0 0 8px 8px', borderTop: expanded ? '1px solid #EBEBEB' : '0' }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        disableGutters
        square
        sx={{
          display: 'flex',
          flexDirection: 'column-reverse',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          sx={{
            '& .MuiAccordionSummary-content': { my: 0, justifyContent: 'center' },
            minHeight: 40,
            bgcolor: '#EBF7FB',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" sx={{ color: 'primary.main', mr: 0.5 }}>
            {`See ${expanded ? 'Less' : 'More'}`}
          </Typography>
          <ExpandMoreIcon sx={{ color: 'primary.main', transform: expanded ? 'rotate(-180deg)' : 'none' }} />
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 2,
            backgroundColor: '#FAFAFA',
            '.MuiBox-root': { backgroundColor: '#FAFAFA' },
            '& > .MuiBox-root + .MuiBox-root': { pt: 2, borderTop: '2px solid #EBEBEB' },
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

function PackItems({ products, productsAttributes }: { products: PackItem[]; productsAttributes: ItemAttributes[] }) {
  return (
    <Box>
      <DraggableCarousel
        sx={{
          mb: '16px',
          gap: '8px',
          scrollbarWidth: 'none',
        }}
      >
        <>
          {products.map((product) => {
            const productAttribute = productsAttributes.find(
              (attribute) => attribute.product_id === product.product?.id
            );
            const colorAttribute = productAttribute?.attributes?.find((attribute) => attribute.name === 'color')?.value;
            const color = product.product?.available_attributes['colors'].find(
              (color) => color.name === colorAttribute
            );

            return (
              <MuiTooltip
                key={product.product?.id}
                title={!product.product?.design_lab_ready ? "This product can't be designed" : ''}
                arrow
                placement="top"
              >
                <Box sx={{ width: 81, height: 133, padding: '8px', border: '1px solid #EBEBEB', borderRadius: '8px' }}>
                  <Image
                    src={product?.product?.image || product?.product?.blank_image || NoImageAvailable}
                    width={59}
                    height={59}
                    alt={'Pack item'}
                    responsive={[{ size: { height: 59, width: 59 } }]}
                    options={{ fit: 'inside' }}
                    dprVariants={[1, 3]}
                    loading="lazy"
                  />
                  {color?.hex && <ColorDisplay color={color as Color} />}
                </Box>
              </MuiTooltip>
            );
          })}
        </>
      </DraggableCarousel>
    </Box>
  );
}
